// Localizado em: src/lib/constants.ts

import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard, Layers, Printer as PrinterIcon, Package, Home, BarChart3, PercentSquare,
  MessageSquare, Menu, SlidersHorizontal, EyeOff, Eye, ChevronRight, FilePlus2,
  WalletMinimal, CalendarPlus, UserPlus, ClipboardList, CalendarDays, DollarSign,
  Users, PackageSearch, Settings2, Archive, CalendarCheck2, Hourglass,
  WalletCards, CalendarX2, TrendingDown, Settings as SettingsIcon, ShoppingCart, UsersRound,
  HelpCircle, Rocket, ClipboardCheck, PackagePlus, Boxes, Tag as TagIcon, Bot, Warehouse, BarChartHorizontalBig, TestTube2, Calculator, BookOpen,
  Wrench, ListChecks, Receipt, PackageOpen, ListTodo,
} from 'lucide-react';
import type { Permission } from './types';

// --- INTERFACES E TIPOS ---

export interface NavItem {
  href: string;
  icon: LucideIcon;
  label: string;
  disabled?: boolean;
  requiredPermission?: Permission;
  /** Para a busca do menu: sinônimos e o que a tela tem dentro ("gastos", "sacola"). */
  palavras?: string[];
  /**
   * Páginas de dentro da tela (cada relatório, cada aba de Cadastros). Não
   * aparecem no menu, só na busca, com a permissão do item.
   */
  subpaginas?: { href: string; label: string; palavras?: string[] }[];
}

export interface BottomNavItem {
  href: string;
  icon: React.ElementType;
  label: string;
  requiredPermission?: Permission;
}

export interface SummaryCardConfig {
  id: string;
  title: string;
  icon: LucideIcon;
  iconBgColor: string;
  iconTextColor: string;
  mainValueColorClass: string;
  defaultVisible: boolean;
  requiredPermission?: Permission;
}

export interface ShortcutCardConfig {
  id: string;
  label: string;
  icon: LucideIcon;
  iconBgColor: string;
  defaultVisible: boolean;
  href?: string;
  requiredPermission?: Permission;
}

export interface AnalyticsChartConfig {
  id: 'statusChart' | 'revenueChart' | 'impressionsChart' | 'pedidosChart';
  title: string;
  defaultVisible: boolean;
  requiredPermission?: Permission;
}

interface ChangelogEntry {
  version: string;
  date: string;
  title: string;
  changes: string[];
}

// --- CONSTANTES DE NAVEGAÇÃO ---

export const NAV_ITEMS: NavItem[] = [
  { href: '/dashboard', icon: LayoutDashboard, label: 'Painel', requiredPermission: 'view_dashboard' },
  { href: '/analytics', icon: BarChartHorizontalBig, label: 'Analytics', requiredPermission: 'view_dashboard' },
  { href: '/farm-manager', icon: PrinterIcon, label: 'Monitor de Farm 3D', requiredPermission: 'view_dashboard' },
  { href: '/caderno-maker', icon: BookOpen, label: 'Caderno do Maker', requiredPermission: 'view_dashboard' },
  { href: '/clientes', icon: Users, label: 'Clientes', requiredPermission: 'manage_orcamentos' },
  { href: '/orcamentos', icon: ClipboardList, label: 'Orçamentos', requiredPermission: 'manage_orcamentos' },
  { href: '/pedidos', icon: ClipboardCheck, label: 'Pedidos', requiredPermission: 'manage_orcamentos' },
  { href: '/impressoes', icon: Bot, label: 'Produção', requiredPermission: 'manage_orcamentos' },
  { href: '/consignado', icon: Warehouse, label: 'Vendas Consignadas', requiredPermission: 'manage_orcamentos' },
  { href: '/financeiro/despesas', icon: TrendingDown, label: 'Despesas', requiredPermission: 'manage_orcamentos' },
  { href: '/calculadora', icon: Calculator, label: 'Calculadora', requiredPermission: 'view_dashboard' },
  { href: '/servicos/cadastros?tab=produtos&action=new', icon: PackagePlus, label: 'Novo Produto', requiredPermission: 'manage_cadastros_produtos' },
  { href: '/ecommerce', icon: ShoppingCart, label: 'Catálogo (E-commerce)', requiredPermission: 'view_ecommerce' },
  { href: '/servicos/cadastros', icon: Archive, label: 'Cadastros', requiredPermission: 'manage_cadastros_produtos' },
  { href: '/relatorios', icon: BarChart3, label: 'Relatórios', requiredPermission: 'view_relatorios' },
  { href: '/checklist-admin', icon: ListChecks, label: 'Gestão de Checklist', requiredPermission: 'manage_configuracoes_sistema' },
  { href: '/configuracoes', icon: SettingsIcon, label: 'Configurações', requiredPermission: 'manage_configuracoes_sistema' },
];

export interface NavGroup {
  category: string;
  items: NavItem[];
}

export const SIDEBAR_NAV_GROUPS: NavGroup[] = [
  {
    category: 'Visão Geral',
    items: [
      { href: '/dashboard', icon: LayoutDashboard, label: 'Painel', requiredPermission: 'view_dashboard', palavras: ['início', 'inicio', 'dashboard', 'resumo'] },
      { href: '/analytics', icon: BarChartHorizontalBig, label: 'Analytics', requiredPermission: 'view_dashboard', palavras: ['gráficos', 'indicadores', 'análise'] },
    ],
  },
  {
    category: 'Vendas & Operações',
    items: [
      // Primeiro a conta, depois o cliente, o orçamento e o pedido.
      { href: '/calculadora', icon: Calculator, label: 'Calculadora', requiredPermission: 'view_dashboard', palavras: ['preço', 'custo', 'precificação', 'margem'] },
      {
        href: '/clientes', icon: Users, label: 'Clientes', requiredPermission: 'manage_orcamentos',
        palavras: ['cliente', 'cadastro de cliente', 'cpf', 'cnpj', 'contato', 'aniversário', 'pessoa física', 'pessoa jurídica', 'empresa', 'ficha', 'sumidos'],
        subpaginas: [
          { href: '/clientes/vincular-pedidos', label: 'Vincular pedidos antigos', palavras: ['pedido sem cliente', 'histórico', 'juntar nomes'] },
        ],
      },
      { href: '/orcamentos', icon: ClipboardList, label: 'Orçamentos', requiredPermission: 'manage_orcamentos', palavras: ['proposta', 'cotação', 'cliente'] },
      { href: '/pedidos', icon: ClipboardCheck, label: 'Pedidos', requiredPermission: 'manage_orcamentos', palavras: ['venda', 'faturar', 'entrega', 'cliente'] },
      { href: '/pedidos?vendaDireta=true', icon: DollarSign, label: 'Venda Direta', requiredPermission: 'manage_orcamentos', palavras: ['balcão', 'pronta entrega', 'avulsa'] },
      {
        href: '/tarefas', icon: ListTodo, label: 'Tarefas', requiredPermission: 'manage_tarefas',
        palavras: ['kanban', 'quadro', 'pedido de trabalho', 'afazeres', 'precificar', 'imprimir', 'pendente', 'fazendo', 'feito', 'colar lista'],
      },
      { href: '/impressoes', icon: Bot, label: 'Produção', requiredPermission: 'manage_orcamentos', palavras: ['impressão', 'imprimir', 'fila', 'a produzir', 'fazer', 'produzir', 'itens'] },
      { href: '/consignado', icon: Warehouse, label: 'Vendas Consignadas', requiredPermission: 'manage_orcamentos', palavras: ['parceiro', 'remessa', 'acerto', 'loja'] },
    ],
  },
  {
    category: 'Produtos & Estoque',
    items: [
      { href: '/servicos/cadastros?tab=produtos&action=new', icon: PackagePlus, label: 'Novo Produto', requiredPermission: 'manage_cadastros_produtos', palavras: ['cadastrar produto', 'criar produto'] },
      { href: '/compras', icon: Receipt, label: 'Compras', requiredPermission: 'view_estoque', palavras: ['nota', 'fornecedor', 'filamento', 'entrada', 'parcela', 'devolução'] },
      { href: '/insumos', icon: PackageOpen, label: 'Embalagens e acessórios', requiredPermission: 'view_estoque', palavras: ['sacola', 'argola', 'caixa', 'ímã', 'etiqueta', 'insumos'] },
      { href: '/ecommerce', icon: ShoppingCart, label: 'Catálogo (E-commerce)', requiredPermission: 'view_ecommerce', palavras: ['loja', 'vitrine', 'site'] },
      {
        href: '/servicos/cadastros', icon: Archive, label: 'Cadastros', requiredPermission: 'manage_cadastros_produtos',
        subpaginas: [
          { href: '/servicos/cadastros?tab=produtos', label: 'Produtos' },
          { href: '/servicos/cadastros?tab=kits', label: 'Kits', palavras: ['combo', 'conjunto', 'pacote', 'pack', 'kit professor'] },
          { href: '/servicos/cadastros?tab=estoque', label: 'Estoque de produtos' },
          { href: '/servicos/cadastros?tab=filaments', label: 'Filamentos', palavras: ['cor', 'pla', 'petg'] },
          { href: '/servicos/cadastros?tab=tipoFilamentos', label: 'Tipos de filamento', palavras: ['material'] },
          { href: '/servicos/cadastros?tab=impressoras', label: 'Impressoras', palavras: ['modelo', 'depreciação'] },
          { href: '/servicos/cadastros?tab=marcas', label: 'Marcas e fornecedores' },
          { href: '/servicos/cadastros?tab=nichos', label: 'Nichos' },
          { href: '/servicos/cadastros?tab=gruposItens', label: 'Grupos de itens' },
        ],
      },
    ],
  },
  {
    category: 'Gestão & Relatórios',
    items: [
      { href: '/financeiro/despesas', icon: TrendingDown, label: 'Despesas', requiredPermission: 'manage_orcamentos', palavras: ['gastos', 'contas', 'energia', 'luz', 'pagar'] },
      {
        href: '/relatorios', icon: BarChart3, label: 'Relatórios', requiredPermission: 'view_relatorios',
        subpaginas: [
          { href: '/relatorios/conciliacao-financeira', label: 'Conciliação Financeira', palavras: ['caixa', 'lucro', 'resultado'] },
          { href: '/relatorios/vendas-por-periodo', label: 'Vendas por Período', palavras: ['faturamento'] },
          { href: '/relatorios/vendas-por-nicho', label: 'Vendas por Nicho', palavras: ['ranking'] },
          { href: '/relatorios/desempenho-produtos', label: 'Desempenho de Produtos', palavras: ['mais vendido'] },
          { href: '/relatorios/consumo-filamento', label: 'Consumo de Filamento' },
          { href: '/relatorios/impressoes-por-material', label: 'Impressões por Material' },
          { href: '/relatorios/estoque-filamentos', label: 'Posição de Estoque', palavras: ['filamento', 'saldo'] },
          { href: '/relatorios/entrada-filamento', label: 'Compras de Filamento', palavras: ['entrada'] },
          { href: '/relatorios/ajustes-estoque', label: 'Ajustes de Estoque' },
          { href: '/relatorios/sugestao-compra', label: 'Sugestão de Compras', palavras: ['repor', 'filamento'] },
          { href: '/relatorios/dizimos', label: 'Dízimos', palavras: ['oferta', 'igreja'] },
          { href: '/relatorios/consignados', label: 'Vendas Consignadas (relatório)', palavras: ['parceiro', 'comissão'] },
          { href: '/relatorios/auditoria', label: 'Auditoria dos Dados', palavras: ['divergência', 'conferência'] },
        ],
      },
      { href: '/checklist-admin', icon: ListChecks, label: 'Gestão de Checklist', requiredPermission: 'manage_configuracoes_sistema', palavras: ['perguntas'] },
      { href: '/configuracoes', icon: SettingsIcon, label: 'Configurações', requiredPermission: 'manage_configuracoes_sistema', palavras: ['empresa', 'cnpj', 'dados fiscais', 'nota fiscal', 'ncm', 'inscrição estadual', 'usuários', 'permissões', 'energia', 'kwh', 'tema', 'notificações'] },
    ],
  },
  {
    category: 'Oficina & Farm 3D',
    items: [
      { href: '/farm-manager', icon: PrinterIcon, label: 'Monitor de Farm 3D', requiredPermission: 'view_dashboard', palavras: ['impressoras', 'máquinas', 'telemetria'] },
      { href: '/farm-manager/manutencao', icon: Wrench, label: 'Manutenção Preventiva', requiredPermission: 'view_dashboard', palavras: ['tarefas', 'bico', 'correia', 'lubrificar'] },
      { href: '/farm-manager/insumos', icon: Boxes, label: 'Insumos da Farm', requiredPermission: 'view_dashboard', palavras: ['peças', 'bicos', 'cola'] },
      { href: '/caderno-maker', icon: BookOpen, label: 'Caderno do Maker', requiredPermission: 'view_dashboard', palavras: ['anotações', 'notas', 'tara', 'calibração', 'dicas'] },
    ],
  },
];

export const MOBILE_SIDEBAR_NAV_ITEMS: NavItem[] = SIDEBAR_NAV_GROUPS.flatMap(group => group.items);

export const BOTTOM_NAV_ITEMS: BottomNavItem[] = [
  { href: '/dashboard', icon: Home, label: 'Início', requiredPermission: 'view_dashboard' },
  { href: '/orcamentos', icon: ClipboardList, label: 'Orçamentos', requiredPermission: 'manage_orcamentos'},
  { href: '/pedidos', icon: ClipboardCheck, label: 'Pedidos', requiredPermission: 'manage_orcamentos' },
  { href: '/servicos/cadastros', icon: Archive, label: 'Cadastros', requiredPermission: 'manage_cadastros_filamentos' },
  { href: '/calculadora', icon: Calculator, label: 'Calculadora', requiredPermission: 'view_dashboard' },
];

// --- CONSTANTES DA APLICAÇÃO ---

export const APP_NAME = "AC3DStudio";
export const APP_VERSION = "4.32.0";

/**
 * Manual de operação, com as telas do sistema, publicado fora daqui. É
 * privado: quem for abrir precisa ter recebido o acesso pelo Compartilhar
 * da própria página.
 */
export const MANUAL_URL = "https://claude.ai/artifact/BtX2AUdJqM3WankAaFskBu";

export const Icons = {
  LayoutDashboard, Layers, Printer: PrinterIcon, Package, Home, BarChart3, PercentSquare,
  MessageSquare, Menu, SlidersHorizontal, EyeOff, Eye, ChevronRight, FilePlus2,
  WalletMinimal, CalendarPlus, UserPlus, ClipboardList, CalendarDays, DollarSign,
  Users, PackageSearch, Settings2, Archive, CalendarCheck2, Hourglass,
  WalletCards, CalendarX2, TrendingDown, Settings: SettingsIcon, ShoppingCart, UsersRound,
  Rocket, HelpCircle, ClipboardCheck, PackagePlus, Boxes, Tag: TagIcon,
  Bot,
  Warehouse,
  BarChartHorizontalBig,
  TestTube2
};

// --- CONFIGURAÇÕES DOS CARDS DO DASHBOARD ---

export const ALL_SUMMARY_CARDS_CONFIG: SummaryCardConfig[] = [
  {
    id: 'faturamentoMes',
    title: 'Faturamento do Mês',
    icon: WalletCards,
    iconBgColor: "bg-emerald-100 dark:bg-emerald-900",
    iconTextColor: "text-emerald-600 dark:text-emerald-400",
    mainValueColorClass: 'text-emerald-600 dark:text-emerald-400',
    defaultVisible: true,
    requiredPermission: 'manage_orcamentos',
  },
  {
    id: 'pendentes',
    title: 'Orçamentos Pendentes',
    icon: Hourglass,
    iconBgColor: "bg-yellow-100 dark:bg-yellow-900",
    iconTextColor: "text-yellow-600 dark:text-yellow-400",
    mainValueColorClass: 'text-yellow-600 dark:text-yellow-400',
    defaultVisible: true,
    requiredPermission: 'manage_orcamentos',
  },
  {
    id: 'aprovadosHoje',
    title: 'Aprovado(s) Hoje',
    icon: CalendarCheck2,
    iconBgColor: "bg-green-100 dark:bg-green-900",
    iconTextColor: "text-green-600 dark:text-green-400",
    mainValueColorClass: 'text-green-600 dark:text-green-400',
    defaultVisible: true,
    requiredPermission: 'manage_orcamentos',
  },
  {
    id: 'faltaImprimir',
    title: 'Falta Imprimir',
    icon: PrinterIcon,
    iconBgColor: "bg-purple-100 dark:bg-purple-900",
    iconTextColor: "text-purple-600 dark:text-purple-400",
    mainValueColorClass: 'text-purple-600 dark:text-purple-400',
    defaultVisible: true,
    requiredPermission: 'manage_orcamentos',
  },
  {
    id: 'aReceber',
    title: 'A Receber',
    icon: DollarSign,
    iconBgColor: "bg-amber-100 dark:bg-amber-900",
    iconTextColor: "text-amber-600 dark:text-amber-400",
    mainValueColorClass: 'text-amber-600 dark:text-amber-400',
    defaultVisible: true,
    requiredPermission: 'manage_orcamentos',
  },
  {
    id: 'aEntregar',
    title: 'A Entregar',
    icon: PackageSearch,
    iconBgColor: "bg-blue-100 dark:bg-blue-900",
    iconTextColor: "text-blue-600 dark:text-blue-400",
    mainValueColorClass: 'text-blue-600 dark:text-blue-400',
    defaultVisible: true,
    requiredPermission: 'manage_orcamentos',
  },
];

export const ALL_SHORTCUT_CARDS_CONFIG: ShortcutCardConfig[] = [
  {
    id: 'novoOrcamento',
    label: 'Novo orçamento',
    icon: FilePlus2,
    iconBgColor: 'bg-primary',
    defaultVisible: true,
    href: '/orcamentos?action=new',
    requiredPermission: 'manage_orcamentos',
  },
  {
    id: 'vendaDireta',
    label: 'Venda Direta',
    icon: DollarSign,
    iconBgColor: 'bg-emerald-500',
    defaultVisible: true,
    href: '/pedidos?vendaDireta=true',
    requiredPermission: 'manage_orcamentos',
  },
  {
    id: 'impressoesAvulsas',
    label: 'Produção',
    icon: Bot,
    iconBgColor: 'bg-cyan-500',
    defaultVisible: true,
    href: '/impressoes',
    requiredPermission: 'manage_orcamentos',
  },
  {
    id: 'vendasConsignadas',
    label: 'Vendas Consignadas',
    icon: Warehouse,
    iconBgColor: 'bg-purple-600',
    defaultVisible: true,
    href: '/consignado',
    requiredPermission: 'manage_orcamentos',
  },
  {
    id: 'verCatalogo',
    label: 'Ver Catálogo',
    icon: ShoppingCart,
    iconBgColor: 'bg-blue-500',
    defaultVisible: false,
    href: '/ecommerce',
    requiredPermission: 'view_ecommerce',
  },
  {
    id: 'cadastroProdutos',
    label: 'Cad. Produtos',
    icon: PackagePlus,
    iconBgColor: 'bg-orange-500',
    defaultVisible: true,
    href: '/servicos/cadastros?tab=produtos&action=new',
    requiredPermission: 'manage_cadastros_produtos',
  },
  {
    id: 'estoque',
    label: 'Estoque',
    icon: Boxes,
    iconBgColor: 'bg-lime-500',
    defaultVisible: true,
    href: '/servicos/estoque',
    requiredPermission: 'view_estoque',
  },
  {
    id: 'cadastros',
    label: 'Cadastros',
    icon: Archive,
    iconBgColor: 'bg-teal-500',
    defaultVisible: false,
    href: '/servicos/cadastros',
    requiredPermission: 'manage_cadastros_filamentos',
  },
  {
    id: 'relatorios',
    label: 'Relatórios',
    icon: BarChart3,
    iconBgColor: 'bg-indigo-500',
    defaultVisible: true,
    href: '/relatorios',
    requiredPermission: 'view_relatorios',
  },
  {
    id: 'configuracoes',
    label: 'Configurações',
    icon: SettingsIcon,
    iconBgColor: 'bg-slate-500',
    defaultVisible: false,
    href: '/configuracoes',
    requiredPermission: 'manage_configuracoes_sistema',
  },
  {
    id: 'calibracoes',
    label: 'Calibrações',
    icon: TestTube2,
    iconBgColor: 'bg-purple-500',
    defaultVisible: true,
    href: '/calibracoes',
    requiredPermission: 'calibracoes:read',
  },
];

export const ALL_ANALYTICS_CHARTS_CONFIG: AnalyticsChartConfig[] = [
    {
      id: 'statusChart',
      title: 'Orçamentos por Status',
      defaultVisible: true,
      requiredPermission: 'manage_orcamentos',
    },
    {
      id: 'pedidosChart',
      title: 'Pedidos por Status',
      defaultVisible: true,
      requiredPermission: 'manage_orcamentos',
    },
    {
      id: 'revenueChart',
      title: 'Faturamento por Período',
      defaultVisible: true,
      requiredPermission: 'manage_orcamentos',
    },
    {
      id: 'impressionsChart',
      title: 'Impressões por Período',
      defaultVisible: true,
      requiredPermission: 'manage_orcamentos',
    },
];

// --- DADOS DO CHANGELOG ---

export const CHANGELOG_DATA: ChangelogEntry[] = [
    {
        version: "4.32.0",
        date: "04/11/2026",
        title: "Foto e dados de cada produto no pedido e na produção",
        changes: [
            "Pedido e orçamento: cada item aparece com a foto e abre uma janela com o nome, as fotos, o link de referência (abrir e copiar) e o que quem imprime precisa saber: tempo, peso, filamento e a nota de impressão. No kit, cada peça aparece com a própria foto e abre a própria janela.",
            "Produção › Por item: foto de cada peça, o resumo do que falta de cada variação (\"3× bandeja amarela · 2× bandeja vermelha\") e uma janela de detalhes com o link de referência e o \"pedido a pedido\".",
            "Corrigido: pedido com kit de peças no mesmo filamento acendia um erro de chave repetida ao abrir.",
            "Farm: peça concluída deixa de ser contada como falha, e o mapa de cores é o da peça em curso.",
            "App Android (0.22.0): a mesma folha de dados do produto no pedido (itens e peças do kit) e na Produção, com foto, link com Abrir e Copiar e os dados de impressão, sem custo, e funciona também sem internet com o que já foi visto.",
        ],
    },
    {
        version: "4.31.0",
        date: "04/11/2026",
        title: "Data de entrega no orçamento",
        changes: [
            "Orçamentos: o vendedor combina a data de entrega ao orçar (já vem para amanhã, 24 h). O cliente vê a entrega prevista na página do orçamento e no PDF e aprova como sempre, só escolhendo a forma de pagamento.",
            "Aprovado, o pedido herda a data. Ela pode ser alterada depois (janela do pedido → Entrega combinada → Alterar) enquanto o pedido espera entrega, e o orçamento acompanha.",
            "Cartão, tabela e janela do pedido e a fila de produção mostram o prazo colorido: atrasado, hoje, amanhã ou em N dias.",
            "A fila de produção e a lista de Pedidos podem ser ordenadas pelo que vence primeiro (menu Ordenar e coluna Entrega). Pedidos sem data ficam no fim.",
            "Aviso no celular na véspera da entrega e quando o prazo passa.",
            "App Android (0.21.0): campo de entrega no orçamento (com calendário), prazo no cartão e na tela do pedido, alterar a data do pedido, prazo na fila de Produção, entrega no PDF e no WhatsApp, e Pedidos ordenável pela entrega.",
        ],
    },
    {
        version: "4.30.0",
        date: "03/11/2026",
        title: "Copiar o link do produto e ordenar a lista no app",
        changes: [
            "Produtos: botão para copiar o link de referência ao lado do campo, no cadastro do produto.",
            "App Android (0.19.0): na lista de Produtos, um ícone na barra de busca ordena por nome (A → Z ou Z → A) ou por data de cadastro, sem ocupar espaço da lista.",
            "App Android: o link do produto na ficha ficou legível, abre ao tocar e tem botão de copiar (também na edição).",
        ],
    },
    {
        version: "4.29.0",
        date: "27/10/2026",
        title: "Cancelar item de um pedido em produção",
        changes: [
            "Pedidos: com o pedido \"A Fazer\" ou \"Fazendo\", dá para cancelar um item (ou parte das unidades) que não vai ser entregue. O valor do pedido e do orçamento é refeito na hora, então o item sai do faturamento. Nenhum material é baixado (não foi produzido), quem produz é avisado e o cancelamento fica registrado no pedido.",
            "A quantidade se escolhe com − e +, com os atalhos Metade e Máximo. O que a produção já marcou como pronto não pode ser cancelado: a tela diz o máximo e como desfazer o \"pronta\".",
            "O app Android (0.18.0) ganhou a mesma tela: \"Cancelar item\" em cada item do pedido, com a prévia do novo valor.",
        ],
    },
    {
        version: "4.28.0",
        date: "27/10/2026",
        title: "A trava e a digital do app, refeitas",
        changes: [
            "App Android (0.17.0): destravar nunca derruba o login. A digital (ou a senha do celular, na mesma janela) abre uma vez só e, se não servir, \"Usar a senha do sistema\" confere a senha ali mesmo. Só \"Sair da conta\" (com confirmação) termina a sessão.",
            "Depois de entrar com a senha, o app não abre mais uma segunda janela de digital: a senha é guardada, cifrada, sem pedir nada. A chave \"Entrar com a digital\" foi para Aparência → Segurança.",
            "\"Sempre que sair\" passa a travar toda vez que o app sai da frente por mais de um segundo, e a trava pela meia-noite saiu: vale só o tempo escolhido.",
            "O servidor só diz \"sua sessão expirou\" quando o Supabase recusa o token; uma falha passageira dele vira \"tente de novo\" e não derruba mais o login do app.",
        ],
    },
    {
        version: "4.27.0",
        date: "26/10/2026",
        title: "Compras inteiras no app",
        changes: [
            "Compras: ao devolver parte de um item, o valor que volta ao caixa é proporcional; ao devolver o que resta, é o que sobrou do valor pago (os centavos fecham). A tela mostrava sempre o proporcional e agora mostra o mesmo que o banco grava.",
            "Produção: filtro de situações na fila \"A produzir\" e cabeçalho mais limpo.",
            "O app Android (0.16.0) ganhou o lançamento de compra (a nota inteira, com a prévia do servidor e o aviso de nota repetida), a devolução ou estorno da nota e a venda de impressora usada. Com isso as Compras estão inteiras no app.",
        ],
    },
    {
        version: "4.26.0",
        date: "26/10/2026",
        title: "Embalagens, Caderno do Maker e Compras no app",
        changes: [
            "Caderno do Maker: esvaziar o texto, o link ou os parâmetros de uma anotação agora apaga de verdade (antes o valor antigo voltava). O custo de energia da anotação de consumo usa o kWh de Configurações, em vez de R$ 0,95 fixo. Tara sem o peso e consumo sem os watts são recusados.",
            "Compras: o período e os filtros passaram a contar pelo dia do estúdio (Rio Branco), como nas Despesas.",
            "O app Android (0.15.0) ganhou Embalagens e acessórios (saldo, reposição, contagem, histórico e cadastro), o Caderno do Maker (anotações, fixar, link do vídeo, \"Pesar agora\" do carretel e custo de energia) e as Compras (notas do período, resumo, por fornecedor com contato e a foto ou o PDF da nota pela câmera).",
        ],
    },
    {
        version: "4.25.0",
        date: "24/10/2026",
        title: "Produção por peça, observação por item e alterações depois de aprovado",
        changes: [
            "Produção: a aba \"A produzir\" mostra o que falta imprimir nos pedidos A Fazer e Fazendo, por item ou por pedido, com o andamento de cada peça (Começar, quantas já saíram, Pronta). A primeira peça que começa leva o pedido a Fazendo.",
            "Observação em cada item do orçamento (\"2 unidades verde e 2 amarelo\"): aparece no orçamento do cliente, no PDF, no pedido e na produção.",
            "Se o orçamento é alterado depois de aprovado, a fila marca \"Alterado depois de aprovado\" com o antes e o depois, e quem produz recebe um aviso.",
            "O app Android (0.14.0) ganhou a tela Produção, o campo de observação no editor de orçamento, o \"Obs.:\" no PDF e o botão \"Ver na produção\" no pedido.",
        ],
    },
    {
        version: "4.24.0",
        date: "23/10/2026",
        title: "Avisos do pedido, Consignado e Configurações no app",
        changes: [
            "Avisos do pedido: em Configurações, o administrador escolhe, para cada fase (A Fazer, Fazendo, Feito...), quem recebe a notificação quando o pedido entra nela, no celular e no navegador. Cada marca muda só aquela pessoa, sem apagar o que outra tela marcou.",
            "Excluir uma loja do consignado deixou de apagar o histórico: saldos, remessas e acertos eram apagados em cascata, e as peças que estavam na loja sumiam da conta. Agora só se exclui loja sem movimento; as demais se desativam.",
            "As preferências de notificação do navegador não apagam mais as de tarefas ao serem gravadas, e registrar o aparelho de novo não religa o que você tinha desligado.",
            "Farm: o Orca Slicer envia direto para a Kobra X pela rede (ponte OctoPrint no agente do NAS), com painel da impressora e câmera ao vivo.",
            "O app Android (0.13.0) ganhou o Consignado (lojas, remessas, acerto com a conta feita pelo servidor, cadastro de loja e comissão por grupo) e as Configurações (parâmetros de custo com o aviso das combinações sem potência, notificações do aparelho, Avisos do pedido e Dados da empresa com a logomarca).",
        ],
    },
    {
        version: "4.23.0",
        date: "21/10/2026",
        title: "Manutenção, perdas e cor divergente na farm",
        changes: [
            "O aviso de manutenção vencida por push finalmente sai: o cron lia a lista sem usuário e era recusado. \"Vence agora\" deixou de aparecer em tarefa que já venceu, e o texto diz quanto passou do limite.",
            "\"Secar filamento em uso\" saiu da manutenção das impressoras (não é manutenção da máquina). A tarefa foi desativada, não apagada.",
            "A caixa de filamentos da farm agora confere também a cor: se a máquina lê uma cor bem diferente da cadastrada, a posição é marcada e entra no \"Precisa de você\", com o motivo (material, cor ou os dois). Só vale com a impressora respondendo.",
            "O app Android (0.12.0) ganhou a tela Manutenção (registrar a tarefa como feita, inclusive em várias máquinas de uma vez), Perdas e baixas (filamento perdido em falhas e baixas pendentes) e o bloco \"Farm agora\" no painel inicial (opcional, em Aparência).",
        ],
    },
    {
        version: "4.22.0",
        date: "20/10/2026",
        title: "O material nasce no orçamento e o estoque baixa em Feito",
        changes: [
            "No orçamento, cada item mostra o material (o do cadastro do produto). Para quem quer 2 unidades numa cor e 2 noutra, \"Trocar ou dividir por cor\" reparte as unidades. É uma sugestão: não baixa o estoque.",
            "Ao marcar o pedido como Feito, abre a confirmação do material usado, já com a sugestão do orçamento. Você confirma ou troca marca, tom e unidades por cor, e só então o filamento sai do estoque.",
            "Começar a produzir deixou de baixar o estoque: é só uma mudança de status.",
            "Farm: ao mandar imprimir com a finalidade Encomenda, escolha o pedido. O pedido e a farm não baixam mais o mesmo filamento duas vezes, e as pendências do Insumos podem ser lançadas de uma vez para a mesma máquina.",
            "O app Android (0.11.0) tem a mesma escolha de material no orçamento e na confirmação do pedido; a regra de repartir as unidades é do servidor, a mesma do site.",
        ],
    },
    {
        version: "4.19.0",
        date: "09/10/2026",
        title: "Dados fiscais e parceiro do consignado como cliente",
        changes: [
            "Dados da Empresa ganham os dados fiscais: CNPJ (com busca na Receita), regime tributário, inscrições estadual e municipal, endereço completo e o NCM padrão dos produtos. É o primeiro passo para emitir nota fiscal ao faturar.",
            "Produtos e kits ganham o campo NCM; em branco, vale o padrão da empresa. Ao digitar, aparece o que o código é na tabela oficial.",
            "O parceiro do consignado pode ser um cliente cadastrado: nome, documento, contato e endereço vêm do cadastro e acompanham as mudanças.",
            "Janelas compridas não passam mais da altura da tela: o título e os botões ficam fixos e só o meio rola.",
            "Endereços antigos (/products, /filaments, /printers...) levam à tela certa em vez de mostrar erro.",
        ],
    },
    {
        version: "4.18.1",
        date: "09/10/2026",
        title: "Cartões arrumados e manual novo",
        changes: [
            "Nos cartões de orçamento e pedido, nome de cliente comprido não empurra mais a situação para fora: o nome ganha a linha inteira.",
            "O manual foi revisto por inteiro, com todas as telas do sistema na ordem do menu.",
        ],
    },
    {
        version: "4.18.0",
        date: "09/10/2026",
        title: "Cadastro de clientes",
        changes: [
            "Novo cadastro de clientes, pessoa física ou jurídica, com busca, filtros, indicadores e a ficha de cada cliente (quanto e o que compra).",
            "Gere um link seguro para o cliente completar os próprios dados; você revisa antes de entrar no cadastro.",
            "Orçamento e venda direta podem ser de um cliente cadastrado ou de consumidor final.",
            "Vincule os pedidos antigos aos clientes, com sugestões pelo nome.",
            "Textos que ficavam apagados com o Windows no modo escuro voltaram a ficar legíveis.",
        ],
    },
    {
        version: "4.17.3",
        date: "08/10/2026",
        title: "Cancelar pedido",
        changes: [
            "Pedido em \"A Fazer\" pode ser cancelado quando o cliente desiste: a janela pede o motivo e avisa que o orçamento não poderá ser reaproveitado.",
            "O orçamento de um pedido cancelado fica travado e mostra o motivo nos detalhes.",
        ],
    },
    {
        version: "4.17.2",
        date: "08/10/2026",
        title: "Fotos com IA no produto",
        changes: [
            "O produto ganha o mesmo estúdio de fotos com IA dos kits: num cenário, a partir das fotos reais dele, pelo app Gemini.",
            "O consumo de filamento do pedido fica anotado no item certo, mesmo com a cor trocada.",
        ],
    },
    {
        version: "4.17.1",
        date: "07/10/2026",
        title: "Relatórios mais certos",
        changes: [
            "Desempenho de produtos passa a contar todas as vendas faturadas, com custo, margem e a visão por peça (kits desmontados).",
            "Dízimo e conciliação calculam o custo dos kits pelos itens e não contam mais material duas vezes.",
            "No kit, digite o preço final em \"Vender por\" e o desconto ou acréscimo é calculado sozinho.",
        ],
    },
    {
        version: "4.17.0",
        date: "05/10/2026",
        title: "Kits",
        changes: [
            "Monte kits com produtos já cadastrados (ex.: suporte + nome + lápis), com desconto ou acréscimo sobre a soma dos itens.",
            "O kit acompanha o preço e o custo dos itens; vendas já feitas guardam o kit como era.",
            "Fotos do kit montado com IA, pelo app Gemini, a partir das fotos dos itens.",
        ],
    },
    {
        version: "4.16.0",
        date: "03/10/2026",
        title: "Nova identidade visual",
        changes: [
            "A logo do sistema foi redesenhada: nítida em qualquer tela, inclusive no tema escuro.",
            "Novos ícones para o navegador, o celular e as notificações.",
        ],
    },
    {
        version: "4.15.2",
        date: "30/09/2026",
        title: "Manual atualizado",
        changes: [
            "O manual explica o novo topo das telas, as abas e filtros presos e o botão de período.",
            "A Calculadora aparece no manual no lugar certo: Vendas & Operações.",
        ],
    },
    {
        version: "4.15.1",
        date: "30/09/2026",
        title: "Confirmações e Dashboard no padrão",
        changes: [
            "Ao excluir ou desfazer, a janela mostra o andamento até terminar, igual em todas as telas.",
            "Os cartões do Dashboard seguem o mesmo visual dos relatórios.",
        ],
    },
    {
        version: "4.15.0",
        date: "30/09/2026",
        title: "Todas as telas com a mesma cara",
        changes: [
            "Toda tela começa pelo mesmo topo: título, voltar e botões sempre no mesmo lugar.",
            "O botão de recolher a barra lateral não fica mais escondido atrás do topo das telas.",
        ],
    },
    {
        version: "4.14.0",
        date: "30/09/2026",
        title: "Abas e filtros sempre à mão em todo o sistema",
        changes: [
            "Consignado, Manutenção, Calibrações, Produtos acabados e Insumos da Farm: as abas ficam presas no topo.",
            "Embalagens, Produção, Caderno e Farm: busca e filtros presos no topo enquanto a lista rola.",
        ],
    },
    {
        version: "4.13.0",
        date: "30/09/2026",
        title: "Filtros sempre à mão em listas e relatórios",
        changes: [
            "Em Pedidos, Orçamentos, Compras e Despesas, a busca e os filtros ficam presos no topo enquanto a lista rola.",
            "Nos relatórios, o período e o botão Gerar relatório ficam numa barra no topo, sempre à mão.",
            "Na Conciliação, troque entre Caixa e Competência sem voltar ao topo.",
        ],
    },
    {
        version: "4.12.0",
        date: "30/09/2026",
        title: "Busca no menu e Cadastros com mais espaço para os dados",
        changes: [
            "Busque qualquer tela pelo menu lateral (ou Ctrl+K): digite 3 letras, como \"diz\" para ir direto ao relatório de Dízimos.",
            "A Calculadora agora fica em Vendas & Operações.",
            "Em Cadastros, as abas e os filtros ficam presos no topo enquanto a lista rola.",
            "Cartões de produto mais compactos: os botões aparecem em cima da foto, e cabem mais produtos na tela.",
        ],
    },
    {
        version: "4.11.9",
        date: "29/09/2026",
        title: "Tabelas, carregamento e listas vazias padronizados",
        changes: [
            "As colunas que ordenam têm a mesma seta em todas as tabelas e funcionam pelo teclado.",
            "O \"carregando\" é igual em todas as telas e diz o que está sendo buscado.",
            "Lista vazia explica o motivo e oferece o próximo passo, como limpar os filtros.",
        ],
    },
    {
        version: "4.11.8",
        date: "29/09/2026",
        title: "Busca com botão de limpar em todas as telas",
        changes: [
            "Todos os campos de busca ganharam o \"X\" para limpar; em Pedidos e Orçamentos ele também volta a mostrar a lista completa.",
        ],
    },
    {
        version: "4.11.7",
        date: "29/09/2026",
        title: "Cartões de número iguais em todo o sistema",
        changes: [
            "Os cartões com totais dos relatórios, Compras, Embalagens, Pedidos e Caderno têm o mesmo formato, e a cor do número indica se é bom (verde), ruim (vermelho) ou atenção (âmbar).",
        ],
    },
    {
        version: "4.11.6",
        date: "29/09/2026",
        title: "Topo das telas padronizado",
        changes: [
            "Dashboard, Farm, Manutenção, Compras, Despesas, Embalagens e Caderno Maker ganharam o mesmo topo das demais telas.",
        ],
    },
    {
        version: "4.11.5",
        date: "29/09/2026",
        title: "Botão de adicionar no celular",
        changes: [
            "O botão verde \"+\" fica sempre acima da barra de páginas; em Produtos, ele ficava por cima dela.",
        ],
    },
    {
        version: "4.11.4",
        date: "29/09/2026",
        title: "Confirmações na janela do sistema",
        changes: [
            "Excluir, enviar remessa, finalizar calibração e outras ações pedem confirmação numa janela do próprio sistema, explicando o que vai acontecer.",
            "Ações que apagam ou desfazem têm o botão vermelho.",
        ],
    },
    {
        version: "4.11.3",
        date: "29/09/2026",
        title: "Paginação igual em todas as listas",
        changes: [
            "As listas mostram quantos itens há (\"11–20 de 57\") e lembram quantos você quer por página.",
            "O PDF do relatório de estoque de filamentos agora sai com todos os filamentos, não só a página da tela.",
            "No cadastro de filamentos, ordenar por densidade ou por ativo voltou a funcionar.",
        ],
    },
    {
        version: "4.11.2",
        date: "29/09/2026",
        title: "Relatórios com atalhos de período",
        changes: [
            "Os relatórios ganharam os atalhos Este mês, 3 meses, 12 meses e Este ano, e lembram a última escolha.",
            "O período Personalizado agora fica guardado ao recarregar a página.",
        ],
    },
    {
        version: "4.11.1",
        date: "29/09/2026",
        title: "Padronização: datas certas e o mesmo formato em todo o sistema",
        changes: [
            "Datas que apareciam um dia antes em Entrada de filamento, Ajustes de estoque e na Conciliação agora mostram o dia certo.",
            "Valores em reais e datas seguem o mesmo formato em todas as telas; valor que não existe aparece como \"—\".",
            "A barra de páginas dos Cadastros ficou igual à de Produtos e Pedidos.",
        ],
    },
    {
        version: "4.11.0",
        date: "28/09/2026",
        title: "Compras: devolução, venda de impressora, nota anexada e fornecedores",
        changes: [
            "Devolva itens ou estorne a nota inteira pelo detalhe da compra: o estoque volta e o dinheiro entra no caixa na data da devolução.",
            "Venda uma impressora usada pela tela de Compras pelo valor que você combinar. A venda entra no caixa, mas fica fora do dízimo.",
            "Anexe a foto ou o PDF da nota a cada compra.",
            "Veja as compras por fornecedor e guarde CNPJ, telefone, e-mail e site de cada um.",
            "Ao faturar, escolha a embalagem do pedido: uma por pedido, e o saldo baixa sozinho.",
            "A depreciação das impressoras usa o valor que você pagou, e a calculadora usa a média da sua frota.",
        ],
    },
    {
        version: "4.10.0",
        date: "28/09/2026",
        title: "Embalagens e acessórios: saldo, custo no preço e baixa na produção",
        changes: [
            "Nova tela Embalagens e acessórios: argola, sacola, caixa e ímã com saldo, custo médio, estoque mínimo e aviso de reposição.",
            "Compre embalagens e acessórios pela Nova compra, com cadastro rápido sem sair da nota.",
            "No cadastro do produto, diga quais embalagens e acessórios vão em cada peça: o custo entra no preço sozinho.",
            "Ao concluir uma produção, o saldo de embalagens e acessórios baixa sozinho; desfazer a produção devolve.",
            "Segurança: funções do banco que respondiam sem necessidade foram fechadas.",
        ],
    },
    {
        version: "4.9.0",
        date: "27/09/2026",
        title: "Compras: impressoras, equipamentos, estrutura e serviços na mesma nota",
        changes: [
            "Nova compra aceita impressora, outros equipamentos, estrutura (gôndola, LED, carimbo) e serviço (frete, conserto), além de filamento.",
            "Impressora comprada vira máquina na farm já com o valor pago, a data, o fornecedor e o número de série.",
            "Cada tipo de compra soma no quadro certo de Despesas: Equipamentos, Infraestrutura ou Outros.",
            "As impressoras lançadas antes como despesa agora aparecem em Compras, ligadas às máquinas da farm.",
        ],
    },
    {
        version: "4.8.0",
        date: "27/09/2026",
        title: "Compras: a nota de compra, com parcelas e filtros",
        changes: [
            "Nova tela Compras (Produtos & Estoque): lance a nota inteira de uma vez, com fornecedor, forma de pagamento, parcelas, frete e desconto.",
            "Compra parcelada aparece em Despesas uma parcela por mês, no mês em que vence.",
            "Ao lançar filamento, a tela mostra como o custo médio do quilo vai mudar antes de gravar.",
            "Filtros por período, fornecedor, material, marca e forma de pagamento, com o total de quilos comprados e o preço médio por kg.",
            "As compras de filamento anteriores foram importadas: 21 notas desde julho de 2025, com os mesmos valores.",
        ],
    },
    {
        version: "4.7.1",
        date: "27/09/2026",
        title: "Despesas completa: grupos, energia das impressoras e datas no dia certo",
        changes: [
            "Despesas renovada: quadros de Insumos, Equipamentos, Infraestrutura e Energia; períodos rápidos (mês, 3 e 12 meses, ano, tudo) e gráfico mês a mês.",
            "A energia das impressoras entra sozinha como despesa todo mês, calculada pelo uso das máquinas: é o valor repassado à conta de luz da casa.",
            "O total de Despesas volta a bater com a Conciliação: duas compras de filamento sem nota fiscal não eram contadas.",
            "Datas de despesa e de compra aparecem no dia certo; editar uma despesa não recua mais a data.",
        ],
    },
    {
        version: "4.7.0",
        date: "27/09/2026",
        title: "Farm até o estoque, auditoria dos dados e telas de gestão revisadas",
        changes: [
            "Menu lateral renovado: os grupos abrem e fecham e ficam como você deixou; o logo leva ao Painel; recolha pelo botão na borda ou com Ctrl+B; o rodapé virou uma linha só, com Ver sistema como, Novidades e Sair.",
            "Manutenção preventiva: os quadros do topo filtram a lista (\"Em dia\" fica oculto por padrão); filtros por impressora e categoria; aba Tarefas com busca e agrupada por plano.",
            "Manutenção vence no primeiro limite que chegar (horas, dias, quilos ou trabalhos), com planos próprios para Kobra X e Bambu A1, registro em lote e aviso por notificação quando vence.",
            "Farm 3D: a baixa de filamento acontece sozinha no fim de cada trabalho, pela medição da impressora, inclusive em impressão colorida; os rolos da caixa ACE aparecem no card.",
            "Enviar impressão pelo sistema, de qualquer lugar: suba o G-code pelo navegador, escolha o arquivo em tela cheia com a prévia da peça, e acompanhe pelo vídeo ao vivo.",
            "Produtos com várias mesas: anexe o G-code de cada placa, o sistema lê cores, gramas e tempo, e despacha a peça pelas máquinas livres; o que não couber fica na fila de espera.",
            "Impressões Avulsas agora é Produção: o histórico do que saiu das máquinas, paginado e com o que falta fazer sempre no começo.",
            "Custo do filamento passa a ser a média ponderada das compras, e não mais o preço da última nota.",
            "Preço desejado de R$ 130,00 fica R$ 130,00 (antes podia virar R$ 130,01).",
            "Orçamento não sai mais duplicado com dois cliques no salvar; excluir mostra que está processando.",
            "Consignado: comissão calculada no servidor, preço travado na remessa, relatório com valores corretos, e a venda consignada entra no dízimo e na conciliação.",
            "Dízimo, estoque e financeiro auditados contra os dados reais: energia passa a entrar no custo, saldo negativo deixa de ser escondido e a mesma impressora deixa de ser cobrada duas vezes.",
            "Segurança: dados que estavam acessíveis pela internet foram fechados.",
            "Produtos podem ser ordenados por nome ou data de cadastro; as grades de Produtos, Orçamentos, Pedidos e Produção não deixam mais buraco no fim da página.",
        ],
    },
    {
        version: "4.6.0",
        date: "08/09/2026",
        title: "Farm 3D com telemetria real: agente local, drivers e manutenção preventiva",
        changes: [
            "Substituída a simulação do Fleet Manager por telemetria real: a versão anterior nunca se comunicou com uma impressora — os adapters apontavam para endpoints inexistentes e a ponte local enviava dados inventados.",
            "Novo agente local (pasta agent/) em Node + Docker, que roda no NAS na mesma rede das impressoras, mantém as conexões vivas e envia telemetria para a nuvem por HTTPS.",
            "Driver Moonraker: Snapmaker U1, Creality K1/K2, Voron e qualquer Klipper.",
            "Driver Bambu Lab: A1, A1 mini, P1 e X1 via MQTT. Exige LAN-only Mode e Developer Mode na impressora.",
            "Driver Anycubic Kobra (3, S1, S1 Max e X): a própria impressora entrega as credenciais num handshake na porta 18910, sem precisar extrair certificado do Slicer Next.",
            "Endpoint de telemetria passa a exigir autenticação. Antes gravava com privilégio de serviço sem barreira nenhuma.",
            "Telemetria parada deixa de parecer telemetria viva: passando de 90 segundos, o card avisa e esmaece os valores.",
            "Separação entre catálogo de modelos e máquinas físicas: quatro Kobra X agora são quatro máquinas com horímetro e manutenção próprios, todas apontando para o mesmo modelo de precificação.",
            "Patrimônio da frota somado por máquina física — o catálogo subestimava, contando quatro unidades como uma.",
            "Manutenção preventiva com quatro tipos de gatilho (horas, dias, quilos extrudados e trabalhos) e plano padrão FDM com 10 tarefas.",
            "Tela de cadastro de tarefas onde cada uma marca em quais máquinas é feita, com intervalo próprio por unidade — filtro de carvão deixa de ser cobrado de impressora aberta.",
            "Máquina fora de operação pode ser removida do monitor sem perder o histórico.",
            "Ferramenta de diagnóstico (scripts/farm-diagnostico.mjs) que valida o token, varre a rede e monta o inventário do agente pronto para colar.",
        ],
    },
    {
        version: "4.2.4",
        date: "24/08/2026",
        title: "Ajuste de Layout Responsivo, Safe Area Insets e Melhorias de Interface",
        changes: [
            "Ajustado o layout do cabeçalho móvel (MobileTopBar) para isolar a área segura da barra de status (safe-area-inset-top), corrigindo o desalinhamento e sobreposição do botão de perfil no Z Fold 8 e dispositivos com notch.",
            "Refatorada a barra de navegação inferior (BottomNav) para suporte dinâmico a safe-area-inset-bottom, prevenindo a compressão de ícones em celulares com navegação por gestos.",
            "Padronizado o dimensionamento dos botões da barra superior (h-9) para alinhamento simétrico perfeito.",
            "Implementada barra flutuante de ações rápidas no rodapé das telas de Orçamentos e Pedidos.",
            "Melhorado o envio direto de orçamentos via WhatsApp e otimizada a paginação nos rodapés.",
            "Aprimorado o aproveitamento de tela no mobile para formulários e páginas de cadastro.",
            "Preservadas as preferências do usuário durante a navegação entre módulos."
        ]
    },
    {
        version: "4.2.3",
        date: "31/01/2025",
        title: "Calculadora de Preços e Otimizações de Interface",
        changes: [
            "Implementada nova Calculadora de Preços 3D com cálculo automático baseado em tipos de filamento e configurações de energia.",
            "Adicionado sistema de privacidade na calculadora com botão de olho para ocultar informações sensíveis durante demonstrações.",
            "Criado sistema de visualização flexível para produtos com três tamanhos (pequeno, médio, grande) similar ao Windows Explorer.",
            "Otimizados todos os relatórios removendo carregamento automático, melhorando performance e experiência do usuário.",
            "Corrigido problema de scroll no Histórico de Versões e Entregas, garantindo visualização completa do conteúdo.",
            "Implementado controle de densidade de cards de produtos com aspect ratios otimizados (1:1 para pequeno, 4:3 para médio/grande).",
            "Adicionados ícones intuitivos para controles de tamanho (Circle, CircleDot, CircleDotDashed) representando a progressão visual."
        ]
    },
    {
        version: "4.2.2",
        date: "29/07/2025",
        title: "Refinamento do Formulário e Correção de Arquivos",
        changes: [
            "Adicionado o campo 'Peso do Rolo (g)' ao formulário de cadastro de filamentos para maior precisão no controle de estoque líquido.",
            "Resolvido bug crítico que impedia a exibição de novos campos devido a arquivos de formulário duplicados e obsoletos.",
            "Corrigida a lógica de importação de componentes para garantir que a versão correta do formulário seja sempre utilizada.",
            "O sistema atinge um novo ponto de controle estável após a refatoração dos componentes de cadastro."
        ]
    },
    {
        version: "4.2.1",
        date: "28/07/2025",
        title: "Controle Flexível de Consumo de Filamento",
        changes: [
            "Implementado um diálogo de confirmação ao mudar status para 'Fazendo' (Pedidos) ou 'Concluído' (Impressões Avulsas).",
            "O diálogo permite ao usuário substituir o filamento padrão do produto por qualquer outro disponível em estoque.",
            "Permite o ajuste fino da quantidade em gramas de cada filamento a ser consumido, garantindo precisão no controle de estoque.",
            "A lista de seleção de filamentos no diálogo agora exibe a quantidade de estoque disponível para cada material.",
            "Melhorada a usabilidade da lista de seleção, que agora é ordenada por Tipo e Cor do filamento.",
            "A dedução do estoque agora é baseada nas informações confirmadas pelo usuário no diálogo, e não mais nos dados do cadastro do produto."
        ]
    },
    {
        version: "4.1.0",
        date: "26/07/2025",
        title: "Controle de Estoque e Catálogo Aprimorados",
        changes: [
            "Implementado sistema de controle para visibilidade de produtos no E-commerce.",
            "Adicionado mecanismo de proteção que impede a exclusão de produtos já utilizados em orçamentos ou pedidos.",
            "Habilitada a desativação de produtos como alternativa à exclusão, preservando o histórico.",
            "Implementado upload direto da logomarca da empresa, substituindo o campo de URL.",
            "Adaptado o sistema para utilizar URLs assinadas para acesso seguro a imagens em buckets privados no Supabase.",
            "Adicionada paginação e filtros avançados (por nicho) na tela de gerenciamento de produtos.",
            "Incluída a opção de visualização de produtos em formato de lista ou grade, para melhor usabilidade.",
        ]
    },
    {
        version: "4.0.0",
        date: "25/07/2025",
        title: "Impressões Avulsas, Relatórios e Melhorias",
        changes: [
            "Adicionado novo módulo 'Impressões Avulsas' para controle de consumo de material em projetos internos, presentes, etc.",
            "Implementado o relatório de 'Impressões por Material' com filtros de período e seleção inteligente de filamentos.",
            "Adicionada flexibilidade no faturamento de pedidos, permitindo ajustar o valor final negociado com o cliente.",
            "Melhorado o formulário de produtos, separando o tempo de produção em campos de 'Horas' e 'Minutos'.",
            "Aprimorada a exibição de cores de filamento, com suporte para nomes compostos (ex: 'Verde Claro').",
            "Atualizada toda a documentação do projeto (README, políticas de banco de dados, esquema) para refletir o estado atual e estável.",
            "Refinada a interface das tabelas com mais tooltips informativos e melhorias visuais."
        ]
    },
    {
        version: "3.3.0",
        date: "17/07/2025",
        title: "Ponto de Controle: Sistema Estável",
        changes: [
            "Corrigida a lógica de exibição de filamentos ativos/inativos nas telas de cadastro e estoque.",
            "Consolidada a busca de dados de filamentos para usar a tabela principal, garantindo a consistência do status 'ativo'.",
            "Resolvido erro crítico na página pública de orçamento, que agora utiliza um cliente Supabase Admin para buscar dados sem exigir login.",
            "Ajustada a função de logout para garantir o redirecionamento correto e completo para a tela de login.",
            "O sistema atinge um estado funcional e estável, servindo como um ponto de controle seguro para futuras implementações."
        ]
    },
    {
        version: "3.2.0",
        date: "15/07/2025",
        title: "Robustez de Dados e Usabilidade",
        changes: [
            "Implementado sistema de 'ativo/inativo' para Filamentos, Impressoras, Marcas e Tipos, evitando exclusão permanente de dados.",
            "Adicionada função no banco de dados com agendamento diário (`pg_cron`) para marcar automaticamente orçamentos pendentes como 'Vencido' após 30 dias.",
            "Interface de Cadastros agora utiliza um `Switch` para ativar/inativar itens, com opção de filtro para visualizar registros inativos.",
            "Melhoradas as mensagens de erro ao tentar excluir um orçamento já vinculado a um pedido, tornando a comunicação mais clara.",
            "Corrigida falha na importação de constantes que causava erro no diálogo de aprovação de orçamentos.",
            "Ajustado o fluxo de entrada da aplicação para sempre redirecionar para a tela de `/login` como ponto de partida.",
            "Consertado o estilo visual do card de atalho 'Cad. Produtos' no dashboard para manter a consistência da interface."
        ]
    },
    {
        version: "3.1.0",
        date: "14/07/2025",
        title: "Segurança, Personalização e Refinamentos de UX",
        changes: [
            "Implementado sistema de segurança com logout automático por inatividade (timeout).",
            "Adicionada tela de configuração para o timeout de sessão e para a personalização do tema (Claro, Escuro, Sistema).",
            "Melhorado o formulário de orçamentos para permitir descontos em valor (R$), com cálculo automático do percentual.",
            "Corrigida a lógica de validação de estoque para produtos com múltiplos filamentos.",
            "Adicionada a exibição da quantidade de estoque na tela de seleção de filamentos no cadastro de produtos.",
            "Restaurado o indicador visual de cor (círculo colorido) nas tabelas de gerenciamento de filamentos.",
        ]
    },
    {
        version: "3.0.0",
        date: "12/07/2025",
        title: "Reestruturação e Estabilização do Core",
        changes: [
            "Corrigido erro crítico de inicialização do servidor ('rimraf: command not found') adicionando o pacote às dependências.",
            "Resolvido erro de runtime em Server Actions ('cookies() should be awaited') ao tornar a inicialização do Supabase assíncrona.",
            "Unificada e reorganizada a estrutura de arquivos do projeto, consolidando todas as rotas de gerenciamento sob o layout `(app)`.",
            "Reformulado o cadastro de produtos para suportar múltiplos filamentos por peça, um requisito essencial para impressões multicoloridas.",
            "Corrigida a lógica de atualização de estoque de filamentos, garantindo que quantidade e preço sejam salvos corretamente tanto em 'Entrada Simples' quanto no fluxo de criação de filamento.",
            "Restaurado e implementado o feedback visual de círculo de cor nas tabelas de filamentos para melhor usabilidade."
        ]
    },
    {
        version: "2.3.0",
        date: "31/07/2025",
        title: "Relatórios Estratégicos e Personalização do Usuário",
        changes: [
            "Criado o novo módulo 'Central de Relatórios' com permissão de acesso.",
            "Implementado o relatório de 'Vendas por Período' com filtros e exportação para CSV.",
            "Implementado o relatório de 'Consumo de Filamento' para análise de uso de material.",
            "Implementado o relatório de 'Desempenho de Produtos' para identificar os itens mais vendidos.",
            "Implementado o relatório de 'Entradas de Estoque' para auditoria de compras e ajustes.",
            "Adicionada a persistência de preferências do dashboard (ordem, visibilidade, tema) no perfil do usuário.",
            "Melhoria na interface e ícones do dashboard para incluir novos atalhos."
        ]
    },
    {
        version: "2.2.0",
        date: "30/07/2025",
        title: "Controle Avançado de Estoque e Rateio de Custos",
        changes: [
            "Criado o módulo de 'Entrada de Estoque por Compra' com rateio automático de frete no custo do filamento.",
            "Implementada uma tela de 'Entrada Simples' para ajustes rápidos de estoque, permitindo adicionar ou remover material (ex: perdas).",
            "Refinada a interface de 'Marcas' para funcionar também como 'Fornecedores', com chaves de controle (is_brand, is_supplier).",
            "Ajustes nos formulários de filamentos e impressoras para filtrar e exibir apenas marcas relevantes, e fornecedores na tela de compra.",
            "Melhoria na usabilidade da tabela de estoque, com um pop-up de edição individual para cada filamento."
        ]
    },
];
